import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/src/renderer/components/ui/Button'
import { TaskEntry } from '../../../../components/TaskEntry'
import { taskToEntryData } from '../../../../utils/taskToEntryData'
import { MovedDestination } from './MovedDestination'
import './InboxMovedNotice.scss'
import type { InboxMovedNoticeProps } from './InboxMovedNotice.types'

/**
 * **Die Meldung über der Liste in Kurz notiert** (Abnahme F-KN3).
 *
 * Wer hier ein Startdatum oder eine Wiederholung setzt, nimmt die Notiz damit
 * aus Kurz notiert — sie ist keine unsortierte mehr. Vorher verschwand die Zeile
 * dabei wortlos, und das sah aus, als wäre sie weg.
 *
 * **Die Aufgabe steht in der Meldung**, nicht mehr in der Liste: So sagt die
 * Meldung nicht nur, dass etwas ging, sondern auch was — und die Liste zeigt
 * schon das Ergebnis. Dasselbe Muster wie in Apple Erinnerungen.
 *
 * Gezeigt wird die **echte Aufgabenzeile** (`TaskEntry`), nicht eine nachgebaute:
 * Sie trägt ihre Marken — allen voran das Datum, das sie gerade hierher gebracht
 * hat — und sieht aus wie das, was eben noch in der Liste stand. Ohne Griffe:
 * Angehakt, aufgeklappt oder gezogen wird hier nichts, die Zeile ist eine
 * Quittung.
 *
 * **`selected` ist hier Optik, keine Auswahl.** Die Zeile soll farbig
 * hervortreten — sie ist der Gegenstand der Meldung. Eine echte Auswahl kann
 * sie nicht sein: Sie steht nicht in der Liste, und ohne Griffe führt von ihr
 * aus keine Handlung weg.
 *
 * **Die Zahl steht als eigenes Stück vor dem Satz**, nicht in ihm: Sie ist fett,
 * und ein `<b>` mitten in einen übersetzten Satz zu weben verlangt `Trans` samt
 * Auszeichnung in jeder Sprachdatei. Beide Sprachen beginnen mit der Zahl; die
 * Mehrzahl entscheidet weiterhin `count`.
 *
 * @example
 * <InboxMovedNotice tasks={geganggene} onConfirm={clear} />
 */
export const InboxMovedNotice: React.FC<InboxMovedNoticeProps> = ({ tasks, onConfirm, onTaskUpdate }) => {
  const { t } = useTranslation()
  if (tasks.length === 0) return null

  return (
    <div className="inbox-moved-notice" role="status">
      <div className="inbox-moved-notice__head">
        <span className="inbox-moved-notice__text">
          <strong className="inbox-moved-notice__count">{tasks.length}</strong>
          {' '}
          {t('quickNotes.moved.message', { count: tasks.length })}
        </span>
        <Button variant="primary" size="sm" onClick={onConfirm}>
          {t('quickNotes.moved.confirm')}
        </Button>
      </div>

      <div className="inbox-moved-notice__tasks">
        {tasks.map(task => (
          <TaskEntry
            key={task.id}
            mode="collapsed"
            task={taskToEntryData(task)}
            selected
            titleAccessory={<MovedDestination task={task} />}
            onContextMenuBadgeUpdate={onTaskUpdate}
          />
        ))}
      </div>
    </div>
  )
}
